import React, { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { ChartContainer, ChartTooltip } from '../ui/chart';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import { Badge } from '../ui/badge';
import { format } from 'date-fns';
export const THRESHOLD_CATEGORIES = ['th1', 'th2', 'rest'];

export type ThresholdCategory = (typeof THRESHOLD_CATEGORIES)[number];

export const getCategoryLabel = (c: ThresholdCategory, start, end) =>
  ({ th1: `Within ${start} hours`, th2: `Within ${end} hours`, rest: `After ${end} hours` })[c];

export const getRange = (c: ThresholdCategory, range: [number, number]) =>
  ({ th1: [0, range[0]], th2: range, rest: [range[1], 'Infinity'] })[c];

export const getCategoryFill = (c: ThresholdCategory) =>
  ({
    th1: 'hsl(var(--chart-1))',
    th2: 'hsl(var(--chart-2))',
    rest: 'hsl(var(--chart-3))',
  })[c];

export interface PerformanceCardProps {
  data: {
    attempt: { th1: number; th2: number; rest: number };
    completed: { th1: number; th2: number; rest: number };
    slaThreshold1: number;
    slaThreshold2: number;
  };
  range: [Date, Date];
  title: string;
  performanceType: 'attempt' | 'completed,';
  companyId?: number;
  carrierId?: number;
}

const CustomTooltip = ({ payload }) => {
  if (payload && payload[0]) {
    return (
      <Badge variant="highlight">
        {payload[0].payload.percentageValue}% {payload[0].payload.category.toLowerCase()}
      </Badge>
    );
  }
  return null;
};

export const PerformanceCard = ({ data, title, range, performanceType, companyId }: PerformanceCardProps) => {
  if (!data[performanceType]) {
    return <></>;
  }
  const history = useHistory();
  const chartData = useMemo(() => {
    const rawData = Object.entries(data[performanceType]).map(([k, v]) => ({
      category: getCategoryLabel(k, data.slaThreshold1, data.slaThreshold2),
      performanceRange: getRange(k, [data.slaThreshold1, data.slaThreshold2]),
      performanceType,
      performanceStartDate: range[0].toISOString(),
      performanceEndDate: range[1].toISOString(),
      to: range[1],
      orders: +v,
      fill: getCategoryFill(k),
    }));

    const total = rawData.reduce((a, c) => a + c.orders, 0);

    const percentages = rawData.map((r) => ({ ...r, percentage: Math.round((r.orders / total) * 100) }));

    const totalPercentage = percentages.reduce((a, c) => a + c.percentage, 0);

    // make sure it adds up to 100 since rounding could mess this up
    if (totalPercentage !== 100) {
      percentages[percentages.length - 1].percentage =
        percentages[percentages.length - 1].percentage + (100 - totalPercentage);
    }

    return percentages.reduce(
      (acc, r) => [
        ...acc,
        {
          ...r,
          category: r.category,
          percentage: [
            acc[acc.length - 1]?.percentage[1] || 0,
            (acc[acc.length - 1]?.percentage[1] || 0) + r.percentage,
          ],
          percentageValue: r.percentage,
          fill: r.fill,
          key: r.category,
        },
      ],
      [],
    );
  }, [data]);

  const totalWithinSla = useMemo(
    () => chartData?.filter((d) => !/^After.*/.test(d.category)).reduce((a, c) => a + c.percentageValue, 0) || '-',
    [chartData],
  );

  const onBarClick = (v) => {
    const companyString = companyId ? `&companyId=${companyId}` : '';
    const startEndString = `&performanceStartDate=${format(v.performanceStartDate, 'yyyy-MM-dd')}&performanceEndDate=${format(v.performanceEndDate, 'yyyy-MM-dd')}`;
    history.push(
      `/orders?performanceType=${performanceType}&performanceRangeStart=${v.performanceRange[0]}&performanceRangeEnd=${v.performanceRange[1]}${startEndString}${companyString}`,
    );
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between">
          {title}{' '}
          <span>
            <span className="text-3xl font-bold">{totalWithinSla}% </span> within 48 hours
          </span>
        </CardTitle>
        <CardDescription></CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={{}}>
          <BarChart
            accessibilityLayer
            data={chartData}
            margin={{
              top: 30,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis dataKey="category" tickLine={false} tickMargin={10} axisLine={false} />
            <YAxis tickLine={false} tickMargin={10} axisLine={false} unit="%" />
            <ChartTooltip cursor={false} content={({ payload }) => <CustomTooltip payload={payload} />} />
            <Bar dataKey="percentage" radius={6} cursor="pointer" onClick={onBarClick} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};
